<script setup lang="ts">
import {onMounted} from "vue";
import type {Tag} from "@/Types";
import {AutoComplete, Button, Checkbox, Label, RadioButton, SelectButton, Slider, ToggleSwitch} from "@/Components/Library";
import DatePicker from "primevue/datepicker";
import {QuestionTypes} from "@/Enums";
import {useTags} from "@/Composables/useTags";

const {group, limits, totalActiveFilters} = defineProps<{
    group: any;
    limits: {responses_count: number};
    totalActiveFilters;
}>();
const filters = defineModel<{
    type: Record<string, boolean>;
    tags: Tag[];
    strict_tags: boolean;
    responses_count: [number, number];
    created_at: Date[] | null;
    publish_from: Date[] | null;
    publish_to: Date[] | null;
    currently_published: "active" | "inactive" | null;
    state: string | null;
    ownership: string | null;
    unanswered: boolean;
}>();
const emit = defineEmits(["apply", "clear"]);

const {tags, fetchTags, loading} = useTags();

onMounted(() => {
    void fetchTags();
});
</script>

<template>
    <div class="mb-4">
        <h3 class="mb-2 font-semibold">Response Status</h3>
        <div class="flex items-center gap-2">
            <Checkbox input-id="unanswered" binary v-model="filters.unanswered" />
            <label for="unanswered">Show only unanswered</label>
        </div>
    </div>
    <div class="mb-4">
        <h3 class="mb-2 font-semibold">Post type</h3>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <template v-for="type in Object.values(QuestionTypes)" :key="type">
                <div class="flex items-center gap-1">
                    <Checkbox :input-id="type.slug" :name="type.name" binary v-model="filters.type[type.slug]" />
                    <label :for="type.slug">{{ type.name }}</label>
                </div>
            </template>
        </div>
    </div>
    <div class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Tags</h3>
        <AutoComplete
            input-id="group_feed_tags_selector"
            multiple
            :items="tags"
            v-model="filters.tags"
            label="Filter by tags"
            placeholder="Start typing to filter tags"
            dropdown
            fluid
        />
        <p v-if="loading" class="mt-1 text-xs text-stone-500">Loading tags...</p>
        <div class="mt-2 flex items-center gap-2">
            <ToggleSwitch input-id="group_feed_strict_tags" v-model="filters.strict_tags" />
            <Label for="group_feed_strict_tags">Strict match (must include all selected tags)</Label>
        </div>
    </div>
    <!--    <div v-if="group.can_see_responses" class="mb-4">-->
    <!--        <h3 class="mb-1 text-sm font-semibold">Number of responses</h3>-->
    <!--        <div class="flex items-center gap-4">-->
    <!--            <span>{{ filters.responses_count[0] }}</span>-->
    <!--            <Slider v-model="filters.responses_count" :min="0" :max="limits.responses_count" range />-->
    <!--            <span>{{ filters.responses_count[1] }}</span>-->
    <!--        </div>-->
    <!--    </div>-->
    <div class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Created</h3>
        <DatePicker
            v-model="filters.created_at"
            show-time
            selectionMode="range"
            date-format="dd/mm/yy"
            hour-format="24"
            :hideOnDateTimeSelect="true"
            fluid
        />
    </div>
    <div class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Published</h3>
        <DatePicker
            v-model="filters.publish_from"
            show-time
            selectionMode="range"
            date-format="dd/mm/yy"
            hour-format="24"
            :hideOnDateTimeSelect="true"
            fluid
        />
    </div>
    <div class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Deadline</h3>
        <DatePicker
            v-model="filters.publish_to"
            show-time
            selectionMode="range"
            date-format="dd/mm/yy"
            hour-format="24"
            :hideOnDateTimeSelect="true"
            fluid
        />
    </div>
    <!--    <div class="mb-4">-->
    <!--        <h3 class="mb-1 text-sm font-semibold">Post status</h3>-->
    <!--        <div class="flex flex-col gap-3">-->
    <!--            <div class="flex items-center gap-1">-->
    <!--                <RadioButton-->
    <!--                    input-id="created_questions"-->
    <!--                    name="created_questions"-->
    <!--                    v-model="filters.state"-->
    <!--                    value="created"-->
    <!--                />-->
    <!--                <Label for="created_questions">Created</Label>-->
    <!--            </div>-->
    <!--            <div class="flex items-center gap-1">-->
    <!--                <RadioButton-->
    <!--                    input-id="archived_questions"-->
    <!--                    name="archived_questions"-->
    <!--                    v-model="filters.state"-->
    <!--                    value="archived"-->
    <!--                />-->
    <!--                <Label for="archived_questions">Archived</Label>-->
    <!--            </div>-->
    <!--        </div>-->
    <!--    </div>-->
    <div v-if="group.can_create || group.params.allow_member_posts" class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Ownership</h3>
        <SelectButton
            v-model="filters.ownership"
            :options="[
                {label: 'Mine', value: 'mine'},
                {label: 'Others', value: 'others'},
            ]"
            option-label="label"
            option-value="value"
            class="w-full"
        />
    </div>
    <div v-if="group.can_admin" class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Currently published</h3>
        <SelectButton
            v-model="filters.currently_published"
            :options="[
                {label: 'Active', value: 'active'},
                {label: 'Inactive', value: 'inactive'},
            ]"
            option-label="label"
            option-value="value"
            class="w-full"
        />
    </div>
    <div v-if="group.can_admin" class="mb-4">
        <h3 class="mb-1 text-sm font-semibold">Archived</h3>
        <SelectButton
            v-model="filters.state"
            :options="[
                {label: 'Include archived', value: 'include_archived'},
                {label: 'Only archived', value: 'only_archived'},
            ]"
            option-label="label"
            option-value="value"
            class="w-full"
        />
    </div>
    <div class="flex gap-2" v-auto-animate>
        <Button class="grow bg-askr-green text-white hover:brightness-110" @click="emit('apply')">Apply</Button>
        <Button
            v-if="totalActiveFilters"
            class="w-10 shrink-0 bg-red-600 text-white hover:brightness-110"
            @click="emit('clear')"
            :icon="{icon: 'mdi:filter-off-outline', width: '24'}"
        />
    </div>
</template>

<style scoped></style>
